import React, { createContext, useState, useEffect, useCallback, act } from "react";
import { getAPIURL, setCachedBusiness, getCachedBusiness } from "../lib/utils";
import { withActiveToken } from "../lib/auth";
import { useRouter } from "next/router";
import { useContext } from "react";
import UserContext from "./user-context";

const BusinessContext = createContext({
  businessList: null,
  activeBusiness: null,
  fetchBusinessList: () => { },
  setActiveBusiness: () => { },
  fetchStaffList: () => { },
  staffList: null,
  setStaffList: () => { },
  teammates: null,
  activeStaffs: null,
  pendingStaffs: null,
  setNotificationContent: () => { },
  notificationContent: {
    showNotification: false,
    header: '',
    message: '',
    icon: null,
  },
  isOwner: false,
});

export const BusinessProvider = (props) => {
  const [businessList, setBusinessList] = useState(null);
  const [activeBusiness, setBusiness] = useState(null);
  const [staffList, setStaffList] = useState(null);
  const { user } = useContext(UserContext);
  const router = useRouter();
  const activeStaffs = staffList? staffList.filter(staff => staff.user.email !== '') : null;
  const pendingStaffs = staffList ? staffList.filter(staff => staff.user.email === '') : null;
  const [notificationContent, setNotificationContent] = useState({
    showNotification: false,
    header: '',
    message: '',
    icon: null,
  });
  const currentUser = staffList ? staffList.find(staff => staff.user.email === user?.email) : null;
  const owner = staffList?.find(staff => staff.role === 'owner')
  const isOwner = currentUser?.role === 'owner';
  const fetchBusinessList = useCallback(() => {
    return withActiveToken(() => {
      fetch(getAPIURL("businesses/"), {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }).then((res) => {
        if (res.ok) {
          return res.json().then((json) => {
            if (json.length === 0) {
              router.push("/businesses/new");
              return;
            }

            setBusinessList(json);

            const businessUuid = router.query.uuid

            if (businessUuid) {
              const activeBusiness = json.find(
                (business) => business.uuid == businessUuid
              );

              if (activeBusiness) {
                setActiveBusiness(activeBusiness);
                return;
              }
            }
            
            const cachedBusiness = getCachedBusiness();

            const activeBusiness = cachedBusiness
              ? json.find((business) => business.uuid == cachedBusiness) ||
              json[0]
              : json[0];
            setActiveBusiness(activeBusiness);
          });
        }
        return res.json().then((json) => Promise.reject(json));
      });
    }, router);
  }, [router]);
  
  const fetchStaffList = useCallback(() => {
    return withActiveToken(() => {
      fetch(getAPIURL(`businesses/${activeBusiness?.uuid}/staff/`), {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }).then((res) => {
        if (res.ok) {
          return res.json().then((json) => {
            setStaffList(json);
          });
        }
        return res.json().then((json) => Promise.reject(json));
      });
    });
  }
  );

  useEffect(() => {
    if (businessList == null && router.isReady) {
      fetchBusinessList();
    }
    if (activeBusiness) {
      fetchStaffList();
    }
  }, [businessList, fetchBusinessList, router.isReady, activeBusiness]);

  function setActiveBusiness(business) {
    setBusiness(business);
    setCachedBusiness(business.uuid);
  }
  // Add the updateBusinessList function
  function updateBusinessList(newList) {
    setBusinessList(newList); // Update the business list state
  }
  const teammates = staffList ? staffList.filter(staff => staff.user.first_name) : null;

  const context = {
    businessList,
    activeBusiness,
    fetchBusinessList,
    setActiveBusiness, 
    updateBusinessList,
    fetchStaffList,
    staffList,
    setStaffList,
    teammates,
    activeStaffs,
    pendingStaffs,
    setNotificationContent,
    notificationContent,
    isOwner,
  };

  return (
    <BusinessContext.Provider value={context}>
      {props.children}
    </BusinessContext.Provider>
  );
};

export default BusinessContext;
